import {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {getAllBusinesses, getEmployeeByBusiness} from "../../services/firestoreService";
import {getEmployeeReadinessIssue, onboardingStatusLabel} from "../../lib/onboarding";

const FILTERS = ["ALL", "PENDING_REVIEW", "SETUP", "CHANGES_REQUESTED", "ACTIVE", "REJECTED", "LEGACY"];

const timestampMillis = (value) => {
    if (typeof value?.toMillis === "function") return value.toMillis();
    if (value?.seconds) return value.seconds * 1000;
    const parsed = new Date(value || 0).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
};

const formatTimestamp = (value) => {
    const millis = timestampMillis(value);
    return millis ? new Date(millis).toLocaleString() : "—";
};

const badgeClass = (status) => ({
    PENDING_REVIEW: "bg-amber-100 text-amber-800",
    ACTIVE: "bg-emerald-100 text-emerald-800",
    CHANGES_REQUESTED: "bg-red-100 text-red-700",
    REJECTED: "bg-red-100 text-red-700",
    SETUP: "bg-blue-100 text-blue-800",
}[status] || "bg-gray-100 text-gray-700");

function AdminBusinessesPage() {
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("ALL");

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            const businessData = await getAllBusinesses();
            const enriched = await Promise.all(businessData.map(async (business) => {
                const employees = await getEmployeeByBusiness(business.id);
                const readyCount = (employees || []).filter(
                    (employee) => !["deactivated", "readonly"].includes(employee.accountStatus) && !getEmployeeReadinessIssue(employee, business),
                ).length;
                return {...business, employeeCount: employees?.length || 0, readyCount};
            }));
            if (!cancelled) {
                setBusinesses(enriched);
                setLoading(false);
            }
        };
        fetchData();
        return () => { cancelled = true; };
    }, []);

    const visibleBusinesses = useMemo(() => businesses
        .filter((business) => {
            const status = business.onboardingStatus || "LEGACY";
            return filter === "ALL" || status === filter;
        })
        .sort((a, b) => {
            const aTime = timestampMillis(a.onboarding?.activationRequestedAt || a.createdAt);
            const bTime = timestampMillis(b.onboarding?.activationRequestedAt || b.createdAt);
            return bTime - aTime;
        }), [businesses, filter]);

    if (loading) return <div className="flex h-screen items-center justify-center text-lg text-gray-500">Loading onboarding queue…</div>;

    return (
        <main className="min-h-screen bg-gray-50 p-4 sm:p-8">
            <div className="mx-auto max-w-6xl">
                <div className="mb-6">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#0d7c5f]">Tiply Admin</p>
                    <h1 className="mt-1 text-3xl font-bold text-gray-900">Business onboarding</h1>
                    <p className="mt-2 text-sm text-gray-600">Review activation requests and monitor setup readiness.</p>
                </div>

                <div className="mb-5 flex flex-wrap gap-2">
                    {FILTERS.map((status) => {
                        const count = businesses.filter((business) => status === "ALL" || (business.onboardingStatus || "LEGACY") === status).length;
                        return <button key={status} type="button" onClick={() => setFilter(status)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${filter === status ? "border-[#0d7c5f] bg-[#0d7c5f] text-white" : "border-gray-200 bg-white text-gray-600"}`}>{status.replaceAll("_", " ")} · {count}</button>;
                    })}
                </div>

                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    {visibleBusinesses.length === 0 ? (
                        <p className="p-8 text-center text-sm text-gray-500">No businesses match this filter.</p>
                    ) : visibleBusinesses.map((business) => {
                        const status = business.onboardingStatus || "LEGACY";
                        const timestamp = business.onboarding?.activationRequestedAt || business.createdAt;
                        return (
                            <article key={business.id} className="grid gap-4 border-b border-gray-100 p-5 last:border-b-0 md:grid-cols-[1fr_auto_auto_auto] md:items-center">
                                <div className="min-w-0">
                                    <p className="truncate font-bold text-gray-900">{business.displayName || business.name || business.legalName || business.id}</p>
                                    <p className="mt-1 text-xs text-gray-500">{business.id} · {business.repEmail || business.ownerEmail || "No owner email"}</p>
                                </div>
                                <span className={`w-fit rounded-full px-3 py-1 text-xs font-bold ${badgeClass(status)}`}>{status === "LEGACY" ? "Legacy active" : onboardingStatusLabel(status)}</span>
                                <div className="text-xs text-gray-500 md:text-right">
                                    <p>{business.readyCount}/{business.employeeCount} payout-ready</p>
                                    <p className="mt-1">{status === "PENDING_REVIEW" ? "Requested" : "Created"}: {formatTimestamp(timestamp)}</p>
                                </div>
                                <Link to={`/admin/business/${business.id}`} className="rounded-lg border border-[#0d7c5f] px-4 py-2 text-center text-sm font-semibold text-[#0d7c5f] hover:bg-[#e8f5f0]">Review</Link>
                            </article>
                        );
                    })}
                </div>
            </div>
        </main>
    );
}

export default AdminBusinessesPage;
